import { ensureOk } from "@/api/apiError";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { components } from "@/api/generated-types";
import type { Agent } from "@/types";

export type PaginatedAgents =
  components["schemas"]["PaginatedResponse_AgentRead_"];
export type AgentAccessFilter = "all" | "granted" | "pending" | "restricted";

export interface AgentQuery {
  q?: string;
  access?: AgentAccessFilter;
  useCase?: string;
  skip?: number;
  limit?: number;
}

function buildQueryString(params: AgentQuery): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.access && params.access !== "all")
    search.set("access", params.access);
  if (params.useCase) search.set("useCase", params.useCase);
  search.set("skip", String(params.skip ?? 0));
  search.set("limit", String(params.limit ?? 12));
  return `?${search.toString()}`;
}

async function fetchAgents(params: AgentQuery): Promise<PaginatedAgents> {
  const response = await httpClient(`/agents${buildQueryString(params)}`);
  await ensureOk(response);
  return response.json() as Promise<PaginatedAgents>;
}

async function fetchAgent(slug: string): Promise<Agent> {
  const response = await httpClient(`/agents/${encodeURIComponent(slug)}`);
  await ensureOk(response);
  return response.json() as Promise<Agent>;
}

async function fetchAgentUseCases(): Promise<string[]> {
  const response = await httpClient("/agents/use-cases");
  await ensureOk(response);
  return response.json() as Promise<string[]>;
}

/** Paginated agent catalogue — search/filter/pagination are resolved server-side. */
export function useAgents(
  params: AgentQuery = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["agents", params],
    queryFn: () => fetchAgents(params),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** Distinct use cases across active agents — powers the store's filter chips. */
export function useAgentUseCases() {
  return useQuery({
    queryKey: ["agents", "use-cases"],
    queryFn: fetchAgentUseCases,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAgent(slug: string | undefined) {
  return useQuery({
    queryKey: ["agents", "detail", slug],
    queryFn: () => fetchAgent(slug as string),
    enabled: Boolean(slug),
    staleTime: 60 * 1000,
  });
}
